import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  MapPin, ChevronDown, ChevronUp, Search, RefreshCw,
  ArrowRightLeft, ClipboardList, Download,
  CheckCircle, Clock, AlertTriangle, Users, Route, FileText,
  Copy, Pencil, Plus, X, Check, ArrowRight, Send, UserPlus,
  LayoutGrid, CheckSquare, ScanLine, Building2, Move, SlidersHorizontal, Trash2,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import INITIAL_ROUTES from '../data/driverRoutes.json';
import RouteSectionsEngine, {
  buildRegistryFromRoutes, buildPlan, nominateSections, detectFlags, groupSections,
} from './RouteSectionsEngine';
import DriverCompletePage from './DriverCompletePage';
import SectionScanReport from './SectionScanReport';
import { matchStreet, looksLikeStreet } from '../utils/streetMatch';

// ── API ───────────────────────────────────────────────────────────────────────
const API = '/.netlify/functions/driver-routes-sb';

async function apiGet() {
  const res = await fetch(API);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function apiPost(body) {
  const res = await fetch(API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ── helpers ──────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function Badge({ children, color = 'var(--hp-gold)', bg }) {
  return (
    <span style={{
      background: bg || 'rgba(201,168,76,0.15)',
      color,
      fontSize: '0.68rem',
      fontWeight: 700,
      padding: '2px 7px',
      borderRadius: 12,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

const AREA_COLORS = {
  CITY: { color: '#60a5fa', bg: 'rgba(59,130,246,0.12)' },
  DARL: { color: '#a78bfa', bg: 'rgba(139,92,246,0.15)' },
  BWAY: { color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
};

// ── Depot codes (D# / Chute / Bay / Outlet / Wave) ─────────────────────────────
// The physical sort attributes AP allocates to each round. These live on the route
// row but until now had no editable field — see the inline editor on the Routes tab
// and the Relocate & Swap tab.
const CODE_FIELDS = [
  { key: 'dNumber',   label: 'D#'     },
  { key: 'chute',     label: 'Chute'  },
  { key: 'bayNumber', label: 'Bay'    },
  { key: 'outlet',    label: 'Outlet' },
  { key: 'wave',      label: 'Wave'   },
];
// A depot position = where in the centre the round is sorted. D# is the round's own
// number and travels with it, so a swap trades everything EXCEPT D#.
const SWAP_FIELDS = ['chute', 'bayNumber', 'outlet', 'wave'];
const FIELD_LABEL = Object.fromEntries(CODE_FIELDS.map(f => [f.key, f.label]));

// Update one round's codes: writes the whole routes table (so the codes persist) and
// logs a 'codes' changelog entry for AP. Optionally renames the round's ADAM code (the
// header identifier — e.g. clearing a "PENDING (Outlet 19A)" placeholder once the real
// code is known); guarded so it can't collide with another round or go blank.
// Returns false (no write) when nothing changed.
function commitCodeChange({ routes, setRoutes, setChangelog, rowIndex, route, next, notes }) {
  // ADAM code rename (only if a non-empty new value was supplied and it actually differs)
  let nextAdam = route.adamCode;
  if (next.adamCode != null) {
    const trimmed = String(next.adamCode).trim();
    if (trimmed && trimmed !== route.adamCode) {
      // Guard against colliding with a DIFFERENT round (ignore this same row).
      if (routes.some((r, i) => i !== rowIndex && r.adamCode === trimmed)) {
        alert(`ADAM code "${trimmed}" already exists on another round. Choose a unique code.`);
        return false;
      }
      nextAdam = trimmed;
    }
  }

  const merged = {};
  CODE_FIELDS.forEach(f => { merged[f.key] = String(next[f.key] ?? route[f.key] ?? '').trim(); });
  const changes = CODE_FIELDS
    .map(f => ({ field: f.label, from: String(route[f.key] ?? ''), to: merged[f.key] }))
    .filter(c => c.from !== c.to);
  if (nextAdam !== route.adamCode) changes.unshift({ field: 'ADAM code', from: route.adamCode, to: nextAdam });
  if (!changes.length) return false;

  const updated = routes.map((r, i) => i === rowIndex ? { ...r, ...merged, adamCode: nextAdam } : r);
  setRoutes(updated);
  const entry = {
    id: `CHG-${Date.now()}`, date: new Date().toISOString(), type: 'codes',
    adamCode: nextAdam, driverLogin: route.driverLogin, area: route.area,
    changes, notes: notes || 'Depot codes updated',
    apNotified: false, apNotifiedDate: null,
  };
  setChangelog(prev => [entry, ...prev]);
  apiPost({ action: 'saveRouteTransfer', routes: updated, entry }).catch(() => {});
  return true;
}

// Swap the depot positions of two rounds (chute/bay/outlet/wave trade places; D# and
// streets stay put). Writes the whole table + logs a 'swap' entry for AP.
function commitSwap({ routes, setRoutes, setChangelog, a, b, notes }) {
  const updated = routes.map(r => {
    if (r.adamCode === a.adamCode) { const n = { ...r }; SWAP_FIELDS.forEach(k => { n[k] = b[k] ?? ''; }); return n; }
    if (r.adamCode === b.adamCode) { const n = { ...r }; SWAP_FIELDS.forEach(k => { n[k] = a[k] ?? ''; }); return n; }
    return r;
  });
  setRoutes(updated);
  const entry = {
    id: `CHG-${Date.now()}`, date: new Date().toISOString(), type: 'swap',
    area: a.area,
    // alias fields so the shared AP-notice header (driver → to driver) renders
    fromDriver: a.driverLogin, fromAdamCode: a.adamCode,
    toDriver: b.driverLogin, toAdamCode: b.adamCode,
    aDriver: a.driverLogin, aAdamCode: a.adamCode,
    bDriver: b.driverLogin, bAdamCode: b.adamCode,
    changes: SWAP_FIELDS.map(k => ({ field: FIELD_LABEL[k], a: a[k] || '—', b: b[k] || '—' })),
    notes: notes || 'Depot position swap',
    apNotified: false, apNotifiedDate: null,
  };
  setChangelog(prev => [entry, ...prev]);
  apiPost({ action: 'saveRouteTransfer', routes: updated, entry }).catch(() => {});
  return entry;
}

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color = 'var(--hp-gold)' }) {
  return (
    <div style={{
      background: 'var(--hp-card)', border: '1px solid var(--hp-border)',
      borderRadius: 10, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 8, display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: `${color}18`, flexShrink: 0,
      }}>
        <Icon size={18} color={color} />
      </div>
      <div>
        <div style={{ fontSize: '1.4rem', fontWeight: 800, color }}>{value}</div>
        <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      </div>
    </div>
  );
}

// ── Smart street search helpers ───────────────────────────────────────────────

function parseQuery(q) {
  const t = q.trim();
  const m = t.match(/^(\d+)\s+(.+)$/);
  if (m) return { num: parseInt(m[1]), name: m[2].toLowerCase() };
  return { num: null, name: t.toLowerCase() };
}

function extractRanges(entry) {
  const ranges = [];
  const rx = /(\d+)\s*[-–]\s*(\d+)/g;
  let m;
  while ((m = rx.exec(entry)) !== null) {
    const a = parseInt(m[1]), b = parseInt(m[2]);
    ranges.push([Math.min(a, b), Math.max(a, b)]);
  }
  return ranges;
}

/* Does this route entry name the street the user asked for?
   The old rule asked only that every query word appear SOMEWHERE in the entry,
   which made "Bridge Rd" return Pyrmont Bridge Rd (a different street, a
   different area, a different driver) and Bridge St as well — while the full
   correct name, "Pyrmont Bridge Road", found nothing at all, because the route
   list writes "rd" and the rule only forgave abbreviations 5+ letters long.
   Street identity now lives in utils/streetMatch: name words AND type must
   agree, prose like "Loading Bay …" is tolerated, and a half-typed query still
   matches forwards so the search stays live as you type. `browse` keeps this box
   wide for a bare fragment — "bridge" still lists Bridge St AND Pyrmont Bridge
   Rd — while a query that names a street ("Bridge Rd") returns only that street. */
function nameMatches(entry, queryName) {
  return matchStreet(entry, queryName, { browse: true }) !== null;
}

function numberInRange(num, lo, hi) {
  const mn = Math.min(lo, hi), mx = Math.max(lo, hi);
  if (num < mn || num > mx) return false;
  const loOdd = lo % 2 !== 0, hiOdd = hi % 2 !== 0;
  if (loOdd === hiOdd) return (num % 2 !== 0) === loOdd;
  return true;
}

function streetMatchesQuery(entry, num, name) {
  if (!nameMatches(entry, name)) return false;
  if (num === null) return true;
  const ranges = extractRanges(entry);
  if (ranges.length === 0) return true;
  return ranges.some(([lo, hi]) => numberInRange(num, lo, hi));
}

// ── Street Summary Panel ──────────────────────────────────────────────────────
// Shown below search bar when a street search returns multiple drivers
function StreetSummaryPanel({ matchedStreets, routes, onScrollTo, search }) {
  const keys = Object.keys(matchedStreets);
  if (keys.length === 0) return null;

  const totalMatches = Object.values(matchedStreets).reduce((n, a) => n + a.length, 0);

  return (
    <div style={{
      background: 'var(--hp-card)',
      border: '1px solid rgba(201,168,76,0.4)',
      borderRadius: 10,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '10px 16px',
        borderBottom: '1px solid var(--hp-border)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: 'rgba(201,168,76,0.06)',
      }}>
        <MapPin size={13} color="var(--hp-gold)" />
        <span style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--hp-gold)' }}>
          {keys.length} driver{keys.length !== 1 ? 's' : ''} deliver to "{search.trim()}"
        </span>
        <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginLeft: 4 }}>
          — {totalMatches} matching entr{totalMatches !== 1 ? 'ies' : 'y'}
        </span>
      </div>

      {/* Driver rows */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {keys.map((rowKey, idx) => {
          const rowIndex = Number(rowKey);
          const route = routes[rowIndex];
          if (!route) return null;
          const hits = matchedStreets[rowKey];
          const ac = AREA_COLORS[route.area] || AREA_COLORS.CITY;
          return (
            <button
              key={rowKey}
              onClick={() => onScrollTo(rowIndex)}
              style={{
                background: 'none',
                border: 'none',
                borderBottom: idx < keys.length - 1 ? '1px solid var(--hp-border)' : 'none',
                padding: '10px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              {/* Driver info */}
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--hp-text)', minWidth: 90 }}>
                  {route.driverLogin}
                </span>
                <Badge color={ac.color} bg={ac.bg}>{route.area}</Badge>
                <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', fontFamily: 'monospace' }}>
                  {route.adamCode}
                </span>
                {route.bayNumber && (
                  <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>Bay {route.bayNumber}</span>
                )}
              </div>

              {/* Matched street entries */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-end' }}>
                {hits.map((s, i) => (
                  <span key={i} style={{
                    fontSize: '0.75rem',
                    color: 'var(--hp-gold)',
                    fontWeight: 600,
                    background: 'rgba(201,168,76,0.12)',
                    padding: '2px 8px',
                    borderRadius: 5,
                    border: '1px solid rgba(201,168,76,0.25)',
                  }}>
                    {s}
                  </span>
                ))}
              </div>

              {/* Jump arrow */}
              <ArrowRight size={14} color="var(--hp-dim)" style={{ flexShrink: 0 }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Routes Tab ────────────────────────────────────────────────────────────────
function RoutesTab({ routes, setRoutes, setChangelog, search, areaFilter, onRenameLogin }) {
  const [expanded, setExpanded] = useState({});
  const [editingStreet, setEditingStreet] = useState(null); // { idx, index } — routes-array index + street index
  const [editValue, setEditValue] = useState('');
  const [addingTo, setAddingTo] = useState(null); // routes-array index
  const [addValue, setAddValue] = useState('');
  const [highlightCode, setHighlightCode] = useState(null); // routes-array index
  const [editingCodes, setEditingCodes] = useState(null); // routes-array index
  const [codeDraft, setCodeDraft] = useState({});          // { dNumber, chute, bayNumber, outlet, wave }

  // Refs for each route card (for scroll-to)
  const cardRefs = useRef({});

  // Row identity is the row's index in `routes` — NOT adamCode, which isn't unique
  // (blank on team-leader/walker/spare rows, and a driver can run several rounds). Keying
  // card state or edit-matching on adamCode wrote edits to the wrong row / every blank row.
  const { filtered, matchedStreets, isStreetSearch, nearMisses } = useMemo(() => {
    const q = search.trim();
    const withIdx = routes.map((r, idx) => ({ r, idx }));
    if (!q) {
      return {
        filtered: areaFilter === 'ALL' ? withIdx : withIdx.filter(({ r }) => r.area === areaFilter),
        matchedStreets: {},
        isStreetSearch: false,
        nearMisses: [],
      };
    }

    const { num, name } = parseQuery(q);
    const lq = q.toLowerCase();
    const isStreetSearch = !routes.some(r =>
      r.driverLogin.toLowerCase().includes(lq) ||
      r.adamCode.toLowerCase().includes(lq) ||
      r.dNumber.toLowerCase().includes(lq) ||
      r.bayNumber === lq
    );

    const matchedStreets = {};
    const result = withIdx.filter(({ r, idx }) => {
      if (areaFilter !== 'ALL' && r.area !== areaFilter) return false;
      if (
        r.driverLogin.toLowerCase().includes(lq) ||
        r.adamCode.toLowerCase().includes(lq) ||
        r.dNumber.toLowerCase().includes(lq) ||
        r.bayNumber.includes(lq)
      ) return true;
      const hits = r.streets.filter(s => streetMatchesQuery(s, num, name));
      if (hits.length > 0) {
        matchedStreets[idx] = hits;
        return true;
      }
      return false;
    });

    /* Nothing found? A street can be missing because a route list spells it
       differently — "Bride rd" is Bridge Rd on a Broadway round. Say so instead
       of showing a blank result; never fold it into the matches, because only a
       person can tell a typo from a genuinely different street. */
    const nearMisses = [];
    if (isStreetSearch && !result.length) {
      routes.forEach((r, idx) => (r.streets || []).forEach((st) => {
        if (looksLikeStreet(st, name) && !nearMisses.some(n => n.idx === idx && n.street === st)) {
          nearMisses.push({ idx, login: r.driverLogin, area: r.area, street: st });
        }
      }));
    }

    return { filtered: result, matchedStreets, isStreetSearch, nearMisses: nearMisses.slice(0, 5) };
  }, [routes, search, areaFilter]);

  const toggle = key => setExpanded(e => ({ ...e, [key]: !e[key] }));
  const isOpen = (idx) => matchedStreets[idx] ? true : expanded[idx];

  // Scroll to a route card (by row index)
  const scrollTo = useCallback((idx) => {
    const el = cardRefs.current[idx];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setExpanded(e => ({ ...e, [idx]: true }));
      setHighlightCode(idx);
      setTimeout(() => setHighlightCode(null), 2000);
    }
  }, []);

  // Save an edited street (rowIndex = index into `routes`, the unique row identity)
  const saveEdit = (rowIndex, streetIndex) => {
    const route = routes[rowIndex];
    const trimmed = editValue.trim();
    if (!trimmed) return;
    const oldStreet = route.streets[streetIndex];
    if (trimmed === oldStreet) { setEditingStreet(null); return; }

    const now = new Date().toISOString();
    const updated = routes.map((r, i) => {
      if (i !== rowIndex) return r;
      const newStreets = [...r.streets];
      newStreets[streetIndex] = trimmed;
      return { ...r, streets: newStreets };
    });
    setRoutes(updated);

    // streets/toAdamCode/toDriver are the ONLY fields saveRouteTransfer persists — an
    // entry built from adamCode/driverLogin/oldStreet alone reaches Supabase blank, and
    // AP's change list then shows nothing. Carry both: these for the round-trip, the
    // rest for this session's UI.
    const entry = {
      id: `CHG-${Date.now()}`,
      date: now,
      type: 'edit',
      adamCode: route.adamCode,
      driverLogin: route.driverLogin,
      area: route.area,
      oldStreet,
      newStreet: trimmed,
      streets: [trimmed],
      toAdamCode: route.adamCode,
      toDriver: route.driverLogin,
      notes: `Range edit: "${oldStreet}" → "${trimmed}"`,
      apNotified: false,
      apNotifiedDate: null,
    };
    setChangelog(prev => [entry, ...prev]);
    apiPost({ action: 'saveRouteTransfer', routes: updated, entry }).catch(() => {});
    setEditingStreet(null);
  };

  // Save a new street added to a route (rowIndex = index into `routes`)
  const saveAdd = (rowIndex) => {
    const route = routes[rowIndex];
    const trimmed = addValue.trim();
    if (!trimmed) return;

    const now = new Date().toISOString();
    const updated = routes.map((r, i) => {
      if (i !== rowIndex) return r;
      return { ...r, streets: [...r.streets, trimmed] };
    });
    setRoutes(updated);

    const entry = {
      id: `CHG-${Date.now()}`,
      date: now,
      type: 'add',
      adamCode: route.adamCode,
      driverLogin: route.driverLogin,
      area: route.area,
      street: trimmed,
      streets: [trimmed],            // the field saveRouteTransfer persists (see saveEdit)
      toAdamCode: route.adamCode,
      toDriver: route.driverLogin,
      notes: 'Street added on the Routes tab',
      apNotified: false,
      apNotifiedDate: null,
    };
    setChangelog(prev => [entry, ...prev]);
    apiPost({ action: 'saveRouteTransfer', routes: updated, entry }).catch(() => {});
    setAddingTo(null);
    setAddValue('');
  };

  // Remove a street entry from a route (rowIndex = index into `routes`). Persists the
  // same way as edit/add — the trimmed street list round-trips via saveRouteTransfer and
  // logs an AP-notifiable 'remove' change so the drop off a driver's round is on record.
  const removeStreet = (rowIndex, streetIndex) => {
    const route = routes[rowIndex];
    const oldStreet = route.streets[streetIndex];
    if (!window.confirm(`Remove "${oldStreet}" from ${route.driverLogin || route.adamCode}'s route?`)) return;

    const now = new Date().toISOString();
    const updated = routes.map((r, i) => {
      if (i !== rowIndex) return r;
      return { ...r, streets: r.streets.filter((_, si) => si !== streetIndex) };
    });
    setRoutes(updated);

    const entry = {
      id: `CHG-${Date.now()}`,
      date: now,
      type: 'remove',
      adamCode: route.adamCode,
      driverLogin: route.driverLogin,
      area: route.area,
      street: oldStreet,
      streets: [oldStreet],   // the street this change concerns (the full list persists via `routes`)
      toAdamCode: route.adamCode,
      toDriver: route.driverLogin,
      notes: `Street removed on the Routes tab: "${oldStreet}"`,
      apNotified: false,
      apNotifiedDate: null,
    };
    setChangelog(prev => [entry, ...prev]);
    apiPost({ action: 'saveRouteTransfer', routes: updated, entry }).catch(() => {});
    if (editingStreet?.adamCode === route.adamCode && editingStreet?.index === streetIndex) setEditingStreet(null);
  };

  // Open / save the inline depot-code editor (rowIndex = index into `routes`)
  const startEditCodes = (rowIndex, route) => {
    setEditingCodes(rowIndex);
    setCodeDraft({
      adamCode: route.adamCode || '',
      dNumber: route.dNumber || '', chute: route.chute || '', bayNumber: route.bayNumber || '',
      outlet: route.outlet || '', wave: route.wave || '',
    });
  };
  const saveCodes = (rowIndex, route) => {
    commitCodeChange({ routes, setRoutes, setChangelog, rowIndex, route, next: codeDraft, notes: 'Depot codes edited on the Routes tab' });
    setEditingCodes(null);
  };

  const totalMatchCount = Object.values(matchedStreets).reduce((n, a) => n + a.length, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

      {/* Street summary panel */}
      {isStreetSearch && Object.keys(matchedStreets).length > 0 && (
        <StreetSummaryPanel
          matchedStreets={matchedStreets}
          routes={routes}
          onScrollTo={scrollTo}
          search={search}
        />
      )}

      {(nearMisses || []).length > 0 && (
        <div style={{
          background: 'var(--hp-card)', border: '1px solid rgba(201,168,76,0.35)',
          borderRadius: 10, padding: '10px 14px', fontSize: '0.8rem', color: 'var(--hp-dim)',
        }}>
          <strong style={{ color: 'var(--hp-text)' }}>No route lists “{search.trim()}”.</strong>{' '}
          Spelt almost the same on a round — a typo on the route list, or a different street?
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {nearMisses.map((n, i) => (
              <button key={i} onClick={() => scrollTo(n.idx)} style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
                color: 'var(--hp-gold)', fontWeight: 600, fontSize: '0.8rem', fontFamily: 'inherit',
              }}>“{n.street}” — {n.login} ({n.area})</button>
            ))}
          </div>
        </div>
      )}

      {/* Results count */}
      <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginBottom: 4 }}>
        Showing {filtered.length} of {routes.length} routes
        {totalMatchCount > 0 && (
          <span style={{ color: 'var(--hp-gold)', marginLeft: 8 }}>
            — {totalMatchCount} matching street{totalMatchCount !== 1 ? 's' : ''} found
          </span>
        )}
      </div>

      {/* Route cards */}
      {filtered.map(({ r, idx }) => {
        const ac = AREA_COLORS[r.area] || AREA_COLORS.CITY;
        const open = isOpen(idx);
        const hits = matchedStreets[idx] || [];
        const hasHits = hits.length > 0;
        const isHighlighted = highlightCode === idx;
        const isAddingHere = addingTo === idx;

        return (
          <div
            key={idx}
            ref={el => { cardRefs.current[idx] = el; }}
            style={{
              background: 'var(--hp-card)',
              border: isHighlighted
                ? '2px solid var(--hp-gold)'
                : hasHits
                  ? '1px solid rgba(201,168,76,0.5)'
                  : '1px solid var(--hp-border)',
              borderRadius: 10,
              overflow: 'hidden',
              transition: 'border 0.3s',
              scrollMarginTop: 120,
            }}
          >
            {/* Header row */}
            <button
              onClick={() => toggle(idx)}
              style={{
                width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10,
                color: 'var(--hp-text)', textAlign: 'left',
              }}
            >
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: '0.85rem', minWidth: 100, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {r.driverLogin}
                  {onRenameLogin && (
                    <span
                      role="button"
                      tabIndex={0}
                      title={`Rename login ${r.driverLogin}`}
                      onClick={e => { e.stopPropagation(); onRenameLogin(r.driverLogin); }}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onRenameLogin(r.driverLogin); } }}
                      style={{ display: 'inline-flex', cursor: 'pointer', color: 'var(--hp-dim)', opacity: 0.7 }}
                      onMouseEnter={e => { e.currentTarget.style.color = 'var(--hp-gold)'; e.currentTarget.style.opacity = 1; }}
                      onMouseLeave={e => { e.currentTarget.style.color = 'var(--hp-dim)'; e.currentTarget.style.opacity = 0.7; }}
                    >
                      <Pencil size={12} />
                    </span>
                  )}
                </span>
                <Badge color={ac.color} bg={ac.bg}>{r.area}</Badge>
                <span style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', fontFamily: 'monospace' }}>{r.adamCode}</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {r.chute && <Badge color="var(--hp-gold)">Chute {r.chute}</Badge>}
                  {r.bayNumber && <Badge color="#f472b6" bg="rgba(244,114,182,0.12)">Bay {r.bayNumber}</Badge>}
                  {r.outlet && r.outlet !== '—' && <Badge color="#22d3ee" bg="rgba(34,211,238,0.12)">Outlet {r.outlet}</Badge>}
                  {r.wave && <Badge color="var(--hp-dim)" bg="rgba(148,163,184,0.1)">Wave {r.wave}</Badge>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                {hasHits && (
                  <span style={{ fontSize: '0.7rem', color: 'var(--hp-gold)', fontWeight: 700 }}>
                    {hits.length} match{hits.length !== 1 ? 'es' : ''}
                  </span>
                )}
                <span style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>{r.streets.length} streets</span>
                {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </div>
            </button>

            {/* Streets list */}
            {open && (
              <div style={{ borderTop: '1px solid var(--hp-border)', padding: '10px 16px 14px' }}>
                {/* Depot codes strip (D# / Chute / Bay / Outlet / Wave) */}
                {(() => {
                  const isEditingCodes = editingCodes === idx;
                  const draftField = (key, placeholder) => (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{placeholder}</span>
                      <input
                        value={codeDraft[key] ?? ''}
                        onChange={e => setCodeDraft(d => ({ ...d, [key]: e.target.value }))}
                        onKeyDown={e => { if (e.key === "Enter") saveCodes(idx, r); if (e.key === "Escape") setEditingCodes(null); }}
                        style={{
                          width: '100%', boxSizing: 'border-box', background: 'var(--hp-surface)',
                          border: '1px solid var(--hp-gold)', borderRadius: 5, padding: '4px 8px',
                          color: 'var(--hp-text)', fontSize: '0.78rem', fontFamily: 'inherit',
                        }}
                      />
                    </label>
                  );
                  return (
                    <div style={{
                      marginBottom: 10, padding: '8px 10px', background: 'var(--hp-surface)',
                      border: '1px solid var(--hp-border)', borderRadius: 8,
                    }}>
                      {isEditingCodes ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <span style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                              ADAM / Round code <span style={{ textTransform: 'none', fontWeight: 400 }}>(the heading — replace a "PENDING …" placeholder here)</span>
                            </span>
                            <input
                              value={codeDraft.adamCode ?? ''}
                              onChange={e => setCodeDraft(d => ({ ...d, adamCode: e.target.value }))}
                              onKeyDown={e => { if (e.key === "Enter") saveCodes(idx, r); if (e.key === "Escape") setEditingCodes(null); }}
                              style={{
                                width: '100%', boxSizing: 'border-box', background: 'var(--hp-surface)',
                                border: '1px solid var(--hp-gold)', borderRadius: 5, padding: '4px 8px',
                                color: 'var(--hp-text)', fontSize: '0.78rem', fontFamily: 'var(--font-mono)',
                              }}
                            />
                          </label>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(72px, 100%), 1fr))', gap: 8 }}>
                            {draftField('dNumber', 'D#')}
                            {draftField('chute', 'Chute')}
                            {draftField('bayNumber', 'Bay')}
                            {draftField('outlet', 'Outlet')}
                            {draftField('wave', 'Wave')}
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              onClick={() => saveCodes(idx, r)}
                              style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.35)', borderRadius: 5, padding: '4px 12px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-green)', display: 'flex', alignItems: 'center', gap: 5 }}
                            >
                              <Check size={11} /> Save codes
                            </button>
                            <button
                              onClick={() => setEditingCodes(null)}
                              style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontSize: '0.72rem', color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 4 }}
                            >
                              <X size={11} /> Cancel
                            </button>
                            <span style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', alignSelf: 'center', marginLeft: 'auto' }}>
                              Saving logs an AP-notifiable change
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Depot codes</span>
                          {CODE_FIELDS.map(f => (
                            <span key={f.key} style={{ fontSize: '0.74rem', color: 'var(--hp-text)' }}>
                              {f.label}{' '}
                              <strong style={{ color: (r[f.key] && r[f.key] !== '—') ? 'var(--hp-gold)' : 'var(--hp-amber)' }}>
                                {r[f.key] && r[f.key] !== '—' ? r[f.key] : '—'}
                              </strong>
                            </span>
                          ))}
                          {onRenameLogin && (
                            <button
                              onClick={() => startEditCodes(idx, r)}
                              title="Edit D# / Chute / Bay / Outlet / Wave"
                              style={{ marginLeft: 'auto', background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: 5, padding: '3px 10px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-gold)', display: 'flex', alignItems: 'center', gap: 5 }}
                            >
                              <SlidersHorizontal size={11} /> Edit codes
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Matched streets highlighted at top */}
                {hasHits && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                      Matching Street{hits.length !== 1 ? 's' : ''}
                    </div>
                    {hits.map((s, i) => (
                      <div key={i} style={{
                        fontSize: '0.82rem', color: 'var(--hp-gold)', padding: '4px 10px',
                        background: 'rgba(34,197,94,0.15)', borderRadius: 6, marginBottom: 3,
                        display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600,
                        border: '1px solid rgba(34,197,94,0.3)',
                      }}>
                        <MapPin size={11} color="var(--hp-gold)" style={{ flexShrink: 0 }} />
                        {s}
                      </div>
                    ))}
                    <div style={{ borderTop: '1px solid var(--hp-border)', margin: '8px 0 6px', fontSize: '0.68rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', paddingTop: 6 }}>
                      All streets on this route
                    </div>
                  </div>
                )}

                {/* Full street list with inline edit */}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {r.streets.map((s, i) => {
                    const isMatch = hits.includes(s);
                    const isEditing = editingStreet?.idx === idx && editingStreet?.index === i;

                    if (isEditing) {
                      return (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          padding: '4px 0',
                          borderBottom: '1px solid var(--hp-border)',
                        }}>
                          <input
                            autoFocus
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === "Enter") saveEdit(idx, i);
                              if (e.key === 'Escape') setEditingStreet(null);
                            }}
                            style={{
                              flex: 1,
                              background: 'var(--hp-surface)',
                              border: '1px solid var(--hp-gold)',
                              borderRadius: 5,
                              padding: '3px 8px',
                              color: 'var(--hp-text)',
                              fontSize: '0.78rem',
                              fontFamily: 'inherit',
                            }}
                          />
                          <button
                            onClick={() => saveEdit(idx, i)}
                            title="Save"
                            style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 5, padding: '3px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                          >
                            <Check size={12} color="var(--hp-green)" />
                          </button>
                          <button
                            onClick={() => setEditingStreet(null)}
                            title="Cancel"
                            style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 5, padding: '3px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                          >
                            <X size={12} color="var(--hp-dim)" />
                          </button>
                        </div>
                      );
                    }

                    return (
                      <div key={i} style={{
                        fontSize: '0.78rem',
                        color: isMatch ? 'var(--hp-gold)' : 'var(--hp-text)',
                        fontWeight: isMatch ? 600 : 400,
                        padding: '3px 0',
                        borderBottom: '1px solid var(--hp-border)',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        <MapPin size={10} color={isMatch ? 'var(--hp-gold)' : 'var(--hp-dim)'} style={{ flexShrink: 0 }} />
                        <span style={{ flex: 1 }}>{s}</span>
                        <button
                          onClick={() => {
                            setEditingStreet({ idx, index: i });
                            setEditValue(s);
                          }}
                          title="Edit this street entry"
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: '2px 4px',
                            cursor: 'pointer',
                            opacity: 0.4,
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: 4,
                            transition: 'opacity 0.15s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                          onMouseLeave={e => e.currentTarget.style.opacity = '0.4'}
                        >
                          <Pencil size={11} color="var(--hp-gold)" />
                        </button>
                        <button
                          onClick={() => removeStreet(idx, i)}
                          title="Remove this street from the route"
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: '2px 4px',
                            cursor: 'pointer',
                            opacity: 0.4,
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: 4,
                            transition: 'opacity 0.15s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                          onMouseLeave={e => e.currentTarget.style.opacity = '0.4'}
                        >
                          <Trash2 size={11} color="var(--hp-red, #EF4444)" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Add Street */}
                {isAddingHere ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                    <input
                      autoFocus
                      value={addValue}
                      onChange={e => setAddValue(e.target.value)}
                      placeholder="e.g. 275 – 297 PITT ST"
                      onKeyDown={e => {
                        if (e.key === "Enter") saveAdd(idx);
                        if (e.key === 'Escape') { setAddingTo(null); setAddValue(''); }
                      }}
                      style={{
                        flex: 1,
                        background: 'var(--hp-surface)',
                        border: '1px solid var(--hp-gold)',
                        borderRadius: 5,
                        padding: '4px 10px',
                        color: 'var(--hp-text)',
                        fontSize: '0.78rem',
                        fontFamily: 'inherit',
                      }}
                    />
                    <button
                      onClick={() => saveAdd(idx)}
                      style={{
                        background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.4)',
                        borderRadius: 5, padding: '4px 10px', cursor: 'pointer',
                        fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-gold)',
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      <Check size={11} /> Add
                    </button>
                    <button
                      onClick={() => { setAddingTo(null); setAddValue(''); }}
                      style={{
                        background: 'var(--hp-surface)', border: '1px solid var(--hp-border)',
                        borderRadius: 5, padding: '4px 8px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center',
                      }}
                    >
                      <X size={11} color="var(--hp-dim)" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setAddingTo(idx); setAddValue(""); }}
                    style={{
                      marginTop: 10,
                      background: 'none',
                      border: '1px dashed var(--hp-border)',
                      borderRadius: 6,
                      padding: '5px 12px',
                      color: 'var(--hp-dim)',
                      fontSize: '0.73rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      width: '100%',
                      justifyContent: 'center',
                      transition: 'border-color 0.15s, color 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--hp-gold)'; e.currentTarget.style.color = 'var(--hp-gold)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--hp-border)'; e.currentTarget.style.color = 'var(--hp-dim)'; }}
                  >
                    <Plus size={11} /> Add Street to This Route
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Searchable driver picker (alphabetical, type-to-filter) ────────────────────
function DriverCombobox({ value, onChange, options, placeholder, showStreetCount }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef(null);

  const selected = options.find(o => o.adamCode === value);
  const display  = selected ? `${selected.driverLogin} | ${selected.adamCode}` : '';

  useEffect(() => {
    const onDoc = e => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const q = query.trim().toLowerCase();
  const sorted = [...options].sort((a, b) =>
    a.driverLogin.localeCompare(b.driverLogin) || a.adamCode.localeCompare(b.adamCode));
  const filtered = q
    ? sorted.filter(o => o.driverLogin.toLowerCase().includes(q) || o.adamCode.toLowerCase().includes(q))
    : sorted;

  const inputStyle = {
    width: '100%', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)',
    borderRadius: 8, padding: '8px 12px', color: 'var(--hp-text)', fontSize: '0.82rem',
    boxSizing: 'border-box',
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        value={open ? query : display}
        onChange={e => { setQuery(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => { setOpen(true); setQuery(''); }}
        placeholder={placeholder || 'Type to search driver…'}
        style={inputStyle}
      />
      <ChevronDown size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--hp-dim)', pointerEvents: 'none' }} />
      {open && (
        <div style={{
          position: 'absolute', zIndex: 60, top: '100%', left: 0, right: 0, marginTop: 4,
          maxHeight: 260, overflowY: 'auto', background: 'var(--hp-card)',
          border: '1px solid var(--hp-border)', borderRadius: 8, boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
        }}>
          {filtered.length === 0 && (
            <div style={{ padding: '10px 12px', fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
              No drivers match “{query}”
            </div>
          )}
          {filtered.map(o => (
            <div
              key={o.adamCode}
              onClick={() => { onChange(o.adamCode); setOpen(false); setQuery(''); }}
              style={{
                padding: '8px 12px', fontSize: '0.82rem', cursor: 'pointer',
                display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center',
                background: o.adamCode === value ? 'rgba(201,168,76,0.12)' : 'transparent',
                color: 'var(--hp-text)',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--hp-surface)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = o.adamCode === value ? 'rgba(201,168,76,0.12)' : 'transparent'; }}
            >
              <span>
                <strong>{o.driverLogin}</strong>{' '}
                <span style={{ color: 'var(--hp-dim)', fontFamily: 'monospace', fontSize: '0.76rem' }}>{o.adamCode}</span>
              </span>
              {showStreetCount && (
                <span style={{ color: 'var(--hp-dim)', fontSize: '0.74rem', flexShrink: 0 }}>
                  {o.streets.length} street{o.streets.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Transfer Tab ──────────────────────────────────────────────────────────────
function TransferTab({ routes, setRoutes, changelog, setChangelog, isViewer, requestedBy, driverRegistry = [], onCreateDriver }) {
  const [fromDriver, setFromDriver] = useState('');
  const [toDriver, setToDriver]     = useState('');
  const [selected, setSelected]     = useState([]);
  const [notes, setNotes]           = useState('');
  const [done, setDone]             = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [mode, setMode]             = useState('streets'); // 'streets' | 'route'
  const [reassignTo, setReassignTo] = useState('');

  const fromRoute = routes.find(r => r.adamCode === fromDriver);
  const toRoute   = routes.find(r => r.adamCode === toDriver);

  const toggleStreet = s => {
    setSelected(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  // Admin: apply transfer immediately
  const handleTransfer = () => {
    if (!fromRoute || !toRoute || selected.length === 0) return;
    const now = new Date().toISOString();
    const updated = routes.map(r => {
      if (r.adamCode === fromDriver) return { ...r, streets: r.streets.filter(s => !selected.includes(s)) };
      if (r.adamCode === toDriver)   return { ...r, streets: [...r.streets, ...selected] };
      return r;
    });
    setRoutes(updated);
    const entry = {
      id: `CHG-${Date.now()}`, date: now, type: 'transfer',
      streets: selected, fromAdamCode: fromDriver, fromDriver: fromRoute.driverLogin,
      toAdamCode: toDriver, toDriver: toRoute.driverLogin,
      area: fromRoute.area, notes, apNotified: false, apNotifiedDate: null,
    };
    setChangelog([entry, ...changelog]);
    apiPost({ action: 'saveRouteTransfer', routes: updated, entry }).catch(() => {});
    setDone(entry);
    setFromDriver(''); setToDriver(''); setSelected([]); setNotes('');
  };

  // Viewer (team leader): submit request for admin approval — does NOT apply immediately
  const handleRequestTransfer = async () => {
    if (!fromRoute || !toRoute || selected.length === 0) return;
    setRequesting(true);
    const entry = {
      id: `REQ-${Date.now()}`,
      date: new Date().toISOString(),
      type: 'transfer_request',
      status: 'pending',
      requestedBy: requestedBy || 'Team Leader',
      streets: selected,
      fromAdamCode: fromDriver, fromDriver: fromRoute.driverLogin,
      toAdamCode: toDriver, toDriver: toRoute.driverLogin,
      area: fromRoute.area, notes,
    };
    try {
      // Log the request (doesn't change routes)
      await apiPost({ action: 'saveRouteTransfer', routes, entry });
      setChangelog([entry, ...changelog]);
      setDone({ ...entry, isRequest: true });
      setFromDriver(''); setToDriver(''); setSelected([]); setNotes('');
    } catch(e) {
      // Still show done even if save fails
      setDone({ ...entry, isRequest: true });
    } finally {
      setRequesting(false);
    }
  };

  // Admin: reassign an ENTIRE route (all streets + bay/chute/outlet/D#/wave) to another
  // driver and retire the departing driver's row. This is the "a driver left, someone takes
  // their whole round" case the street-by-street move can't do — that leaves bay/chute/outlet
  // behind on the old row, and a shell destination with a blank ADAM code can't even be
  // selected (which is why a naive transfer silently no-ops). Route identity (ADAM code / bay
  // / chute / outlet) stays put; only the owning login changes, mirroring how AP keeps the
  // round and reissues the driver.
  const handleReassign = () => {
    const to = reassignTo.trim().toUpperCase();
    if (!fromRoute || !to || to === fromRoute.driverLogin.toUpperCase()) return;
    const receivingReal = routes.filter(r => r.driverLogin.toUpperCase() === to && (r.streets || []).length > 0);
    let msg = `Reassign the entire ${fromRoute.area} route ${fromRoute.adamCode} — bay ${fromRoute.bayNumber || '—'}, chute ${fromRoute.chute || '—'}, outlet ${fromRoute.outlet || '—'}, ${fromRoute.streets.length} streets — from ${fromRoute.driverLogin} to ${to}?\n\n${fromRoute.driverLogin}'s route row will be retired.`;
    if (receivingReal.length) msg += `\n\nNote: ${to} already owns ${receivingReal.length} route(s) with streets — they keep those and also take this one.`;
    if (!window.confirm(msg)) return;
    // Drop any EMPTY shell rows the receiving login already had (e.g. a blank pre-created
    // row with a missing ADAM code), then move the whole departing route onto that login.
    const updated = routes
      .filter(r => !(r.driverLogin.toUpperCase() === to && (r.streets || []).length === 0))
      .map(r => r.adamCode === fromRoute.adamCode ? { ...r, driverLogin: to } : r);
    setRoutes(updated);
    const now = new Date().toISOString();
    const entry = {
      id: `CHG-${Date.now()}`, date: now, type: 'transfer',
      streets: fromRoute.streets, fromAdamCode: fromRoute.adamCode, fromDriver: fromRoute.driverLogin,
      toAdamCode: fromRoute.adamCode, toDriver: to, area: fromRoute.area,
      notes: (notes ? notes + ' — ' : '') + `Whole-route reassignment (bay ${fromRoute.bayNumber || '—'}, chute ${fromRoute.chute || '—'}, outlet ${fromRoute.outlet || '—'}, ${fromRoute.streets.length} streets); ${fromRoute.driverLogin} retired`,
      apNotified: false, apNotifiedDate: null,
    };
    setChangelog([entry, ...changelog]);
    apiPost({ action: 'saveRouteTransfer', routes: updated, entry }).catch(() => {});
    setDone({ ...entry, wholeRoute: true, streetCount: fromRoute.streets.length });
    setFromDriver(''); setReassignTo(''); setNotes('');
  };

  if (done) {
    return (
      <div style={{
        background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 10,
        padding: 24, display: 'flex', flexDirection: 'column', gap: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: done.isRequest ? '#f59e0b' : 'var(--hp-green)' }}>
          {done.isRequest ? <Clock size={20} /> : <CheckCircle size={20} />}
          <span style={{ fontWeight: 700 }}>
            {done.isRequest
              ? `Transfer request submitted — awaiting admin approval`
              : done.wholeRoute
                ? `Whole route reassigned — ${done.streetCount} streets + bay/chute/outlet moved to ${done.toDriver}`
                : `Transfer recorded — ${done.streets.length} street${done.streets.length > 1 ? 's' : ''} moved`}
          </span>
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
          From <strong style={{ color: 'var(--hp-text)' }}>{done.fromDriver}</strong> ({done.fromAdamCode}) →{' '}
          <strong style={{ color: 'var(--hp-text)' }}>{done.toDriver}</strong> ({done.toAdamCode})
        </div>
        {done.isRequest ? (
          <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: '0.78rem', color: 'var(--hp-amber)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Clock size={14} />
            Your request has been logged. Admin will review and apply the transfer — no streets have been moved yet.
          </div>
        ) : (
          <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: '0.78rem', color: 'var(--hp-amber)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={14} />
            Remember to notify Australia Post. Go to the Change Log tab to generate the AP notification.
          </div>
        )}
        <button
          onClick={() => setDone(null)}
          style={{ alignSelf: 'flex-start', background: 'var(--hp-gold)', color: '#000', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}
        >
          {done.isRequest ? 'Submit Another Request' : 'Make Another Transfer'}
        </button>
      </div>
    );
  }

  const sel = {
    width: '100%', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)',
    borderRadius: 8, padding: '8px 12px', color: 'var(--hp-text)', fontSize: '0.82rem',
  };

  // Move-streets vs reassign-whole-route (admin only — team leaders request street moves).
  const segBtn = (active) => ({
    flex: 1, padding: '8px 12px', borderRadius: 7, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700,
    border: '1px solid ' + (active ? 'rgba(201,168,76,0.5)' : 'var(--hp-border)'),
    background: active ? 'rgba(201,168,76,0.14)' : 'var(--hp-surface)',
    color: active ? 'var(--hp-gold)' : 'var(--hp-dim)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  });
  const modeToggle = !isViewer ? (
    <div style={{ display: 'flex', gap: 8 }}>
      <button onClick={() => { setMode('streets'); }} style={segBtn(mode === 'streets')}>
        <MapPin size={13} /> Move streets
      </button>
      <button onClick={() => { setMode('route'); setSelected([]); }} style={segBtn(mode === 'route')}>
        <ArrowRightLeft size={13} /> Reassign whole route
      </button>
    </div>
  ) : null;

  // ── Reassign-whole-route mode ───────────────────────────────────────────────
  if (mode === 'route' && !isViewer) {
    // Candidates = every ACTIVE driver, not just the logins that already own a route row.
    // Building this from `routes` alone hid exactly the driver you most often need here:
    // the one who just handed their old round over and so owns nothing (PENAOYARL after
    // CITY_D1 went to PENAB). Drivers with no round are listed first — they're the ones
    // waiting for one — and each option carries the name so two similar logins are
    // distinguishable. Free text still wins: a brand-new login can be typed straight in.
    const routeLogins = new Set(routes.map(r => (r.driverLogin || '').toUpperCase()).filter(Boolean));
    const withStreets = new Set(routes.filter(r => (r.streets || []).length > 0).map(r => (r.driverLogin || '').toUpperCase()));
    const known = new Map();
    driverRegistry.forEach(d => known.set(d.login, d));
    const allLogins = [...new Set([...known.keys(), ...routeLogins])]
      .filter(l => !fromRoute || l !== fromRoute.driverLogin.toUpperCase())
      .sort((a, b) => (withStreets.has(a) - withStreets.has(b)) || a.localeCompare(b));
    const loginHint = (l) => {
      const d = known.get(l);
      const bits = [];
      if (d && d.name) bits.push(d.name);
      bits.push(withStreets.has(l) ? `already on ${d && d.area ? d.area : 'a round'}` : 'no round');
      return bits.join(' · ');
    };
    const toUp = reassignTo.trim().toUpperCase();
    const canReassign = fromRoute && toUp && toUp !== fromRoute.driverLogin.toUpperCase();
    const receivingReal = toUp ? routes.filter(r => r.driverLogin.toUpperCase() === toUp && (r.streets || []).length > 0) : [];
    const badge = { fontSize: '0.72rem', fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: 'rgba(201,168,76,0.12)', color: 'var(--hp-gold)' };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {modeToggle}
        <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, padding: '10px 14px', fontSize: '0.78rem', color: 'var(--hp-amber)', display: 'flex', gap: 8 }}>
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          Hands the entire round — every street plus its bay, chute, outlet and D# — to another driver, and retires the departing driver. The route keeps its ADAM code (same round, new driver). Creates a change requiring Australia Post notification.
        </div>
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Departing Driver (hands over the route)
            </label>
            <DriverCombobox
              value={fromDriver}
              onChange={setFromDriver}
              options={routes.filter(r => r.streets.length > 0)}
              placeholder="Type the leaving driver's login…"
              showStreetCount
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Receiving Driver (takes the route)
            </label>
            <input
              list="reassign-logins"
              value={reassignTo}
              onChange={e => setReassignTo(e.target.value.toUpperCase())}
              placeholder="Type or pick a login…"
              style={{ ...sel, textTransform: 'uppercase' }}
            />
            <datalist id="reassign-logins">
              {allLogins.map(l => <option key={l} value={l}>{loginHint(l)}</option>)}
            </datalist>
            <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>
              Any active driver — including one who currently has no round — or a brand-new login typed in.
              An empty placeholder row for this login is absorbed automatically.
            </span>
          </div>
        </div>

        {fromRoute && (
          <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Route being reassigned</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: '0.82rem' }}>
              <strong style={{ color: 'var(--hp-text)' }}>{fromRoute.adamCode}</strong>
              <span style={{ color: 'var(--hp-dim)' }}>·</span>
              <span>{fromRoute.area}{fromRoute.dNumber ? ' / ' + fromRoute.dNumber : ''}</span>
              <span style={badge}>Chute {fromRoute.chute || '—'}</span>
              <span style={badge}>Bay {fromRoute.bayNumber || '—'}</span>
              <span style={badge}>Outlet {fromRoute.outlet || '—'}</span>
              <span style={badge}>{fromRoute.streets.length} streets</span>
            </div>
            {canReassign && (
              <div style={{ fontSize: '0.82rem', color: 'var(--hp-text)' }}>
                <strong style={{ color: 'var(--hp-amber)' }}>{fromRoute.driverLogin}</strong> → <strong style={{ color: 'var(--hp-green)' }}>{toUp}</strong>
                <span style={{ color: 'var(--hp-dim)' }}> · {fromRoute.driverLogin} will be retired</span>
              </div>
            )}
            {receivingReal.length > 0 && (
              <div style={{ fontSize: '0.76rem', color: 'var(--hp-amber)', display: 'flex', gap: 6, alignItems: 'center' }}>
                <AlertTriangle size={13} /> {toUp} already owns {receivingReal.length} route(s) with streets — they'll keep those and also take this one.
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Notes (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="e.g. driver left the business; replacement takes over the whole round" style={{ ...sel, resize: 'vertical', fontFamily: 'inherit' }} />
        </div>

        <button
          onClick={handleReassign}
          disabled={!canReassign}
          style={{
            background: canReassign ? 'var(--hp-gold)' : 'var(--hp-border)',
            color: canReassign ? '#000' : 'var(--hp-dim)',
            border: 'none', borderRadius: 8, padding: '10px 22px',
            fontWeight: 700, fontSize: '0.85rem', cursor: canReassign ? 'pointer' : 'default',
            alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          <ArrowRightLeft size={15} /> Reassign Whole Route
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {modeToggle}
      <div style={{
        background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)',
        borderRadius: 8, padding: '10px 14px', fontSize: '0.78rem', color: 'var(--hp-amber)',
        display: 'flex', gap: 8,
      }}>
        <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
        Transferring streets will update the live route data and create a change log entry requiring Australia Post notification.
      </div>

      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            From Driver (current owner)
          </label>
          <DriverCombobox
            value={fromDriver}
            onChange={code => { setFromDriver(code); setSelected([]); }}
            options={routes.filter(r => r.streets.length > 0)}
            placeholder="Type a login or ADAM code…"
            showStreetCount
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            To Driver (receiving)
          </label>
          <DriverCombobox
            value={toDriver}
            onChange={setToDriver}
            options={routes.filter(r => r.adamCode !== fromDriver)}
            placeholder="Type a login or ADAM code…"
            showStreetCount
          />
          {!isViewer && onCreateDriver && (
            <button
              type="button"
              onClick={onCreateDriver}
              style={{
                alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0,
                color: 'var(--hp-gold)', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 5,
              }}
            >
              <UserPlus size={12} /> Driver not listed? Create a new driver
            </button>
          )}
        </div>
      </div>

      {fromRoute && fromRoute.streets.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Select Streets to Transfer
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setSelected([...fromRoute.streets])}
                style={{ background: 'none', border: '1px solid var(--hp-border)', color: 'var(--hp-dim)', fontSize: '0.72rem', padding: '3px 10px', borderRadius: 6, cursor: 'pointer' }}>
                Select All
              </button>
              <button onClick={() => setSelected([])}
                style={{ background: 'none', border: '1px solid var(--hp-border)', color: 'var(--hp-dim)', fontSize: '0.72rem', padding: '3px 10px', borderRadius: 6, cursor: 'pointer' }}>
                Clear
              </button>
            </div>
          </div>
          <div style={{
            background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8,
            maxHeight: 280, overflowY: 'auto', padding: 8,
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))', gap: 4,
          }}>
            {fromRoute.streets.map(s => {
              const checked = selected.includes(s);
              return (
                <label key={s} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px',
                  borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem',
                  background: checked ? 'rgba(201,168,76,0.12)' : 'transparent',
                  border: checked ? '1px solid rgba(201,168,76,0.35)' : '1px solid transparent',
                  color: checked ? 'var(--hp-gold)' : 'var(--hp-text)',
                }}>
                  <input type="checkbox" checked={checked} onChange={() => toggleStreet(s)} style={{ accentColor: 'var(--hp-gold)' }} />
                  <MapPin size={10} style={{ flexShrink: 0 }} />
                  {s}
                </label>
              );
            })}
          </div>
          {selected.length > 0 && (
            <div style={{ fontSize: '0.78rem', color: 'var(--hp-gold)' }}>
              {selected.length} street{selected.length > 1 ? 's' : ''} selected
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Notes (optional)
        </label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={2}
          placeholder="Reason for transfer, e.g. driver resignation, workload rebalance..."
          style={{ ...sel, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </div>

      {isViewer ? (
        // Team leaders: request only — admin must approve before routes change
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-amber)', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Clock size={13} />
            Transfers require admin approval. Submitting this will send a request — no streets will move until approved.
          </div>
          <button
            onClick={handleRequestTransfer}
            disabled={!fromDriver || !toDriver || selected.length === 0 || requesting}
            style={{
              background: (fromDriver && toDriver && selected.length > 0) ? '#f59e0b' : 'var(--hp-border)',
              color: (fromDriver && toDriver && selected.length > 0) ? '#000' : 'var(--hp-dim)',
              border: 'none', borderRadius: 8, padding: '10px 22px',
              fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
              alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <Send size={15} />
            {requesting ? 'Submitting…' : 'Request Transfer'}
          </button>
        </div>
      ) : (
        // Admins: apply immediately
        <button
          onClick={handleTransfer}
          disabled={!fromDriver || !toDriver || selected.length === 0}
          style={{
            background: (fromDriver && toDriver && selected.length > 0) ? 'var(--hp-gold)' : 'var(--hp-border)',
            color: (fromDriver && toDriver && selected.length > 0) ? '#000' : 'var(--hp-dim)',
            border: 'none', borderRadius: 8, padding: '10px 22px',
            fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
            alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          <ArrowRightLeft size={15} />
          Confirm Transfer
        </button>
      )}
    </div>
  );
}

// ── Relocate & Swap Tab ───────────────────────────────────────────────────────
// Depot moves: relocate one round to a new bay/chute/outlet/wave, or swap two rounds'
// physical sort positions. Both persist via saveRouteTransfer and log an AP-notifiable
// changelog entry (Depot Codes / Position Swap). Admin only.
function RelocateSwapTab({ routes, setRoutes, changelog, setChangelog, isViewer }) {
  const [mode, setMode]       = useState('relocate'); // 'relocate' | 'swap'
  const [target, setTarget]   = useState('');         // adamCode being relocated
  const [draft, setDraft]     = useState({});         // relocate field draft
  const [swapA, setSwapA]     = useState('');
  const [swapB, setSwapB]     = useState('');
  const [notes, setNotes]     = useState('');
  const [done, setDone]       = useState(null);

  const targetRoute = routes.find(r => r.adamCode === target);
  const aRoute = routes.find(r => r.adamCode === swapA);
  const bRoute = routes.find(r => r.adamCode === swapB);

  // Load the picked round's current codes into the relocate draft
  const pickTarget = (code) => {
    setTarget(code);
    const r = routes.find(x => x.adamCode === code);
    setDraft(r ? { dNumber: r.dNumber || '', chute: r.chute || '', bayNumber: r.bayNumber || '', outlet: r.outlet || '', wave: r.wave || '' } : {});
  };

  const relocateChanges = targetRoute
    ? CODE_FIELDS.map(f => ({ field: f.label, key: f.key, from: String(targetRoute[f.key] ?? ''), to: String(draft[f.key] ?? '').trim() })).filter(c => c.from !== c.to)
    : [];

  const doRelocate = () => {
    if (!targetRoute || relocateChanges.length === 0) return;
    commitCodeChange({ routes, setRoutes, setChangelog, route: targetRoute, next: draft, notes: (notes.trim() || 'Depot relocation') });
    setDone({ kind: 'relocate', driver: targetRoute.driverLogin, adamCode: targetRoute.adamCode, changes: relocateChanges });
    setTarget(''); setDraft({}); setNotes('');
  };

  const doSwap = () => {
    if (!aRoute || !bRoute || aRoute.adamCode === bRoute.adamCode) return;
    if (!window.confirm(`Swap depot positions between ${aRoute.driverLogin} (${aRoute.adamCode}) and ${bRoute.driverLogin} (${bRoute.adamCode})?\n\nChute / Bay / Outlet / Wave will trade places. D# and streets stay with each round.`)) return;
    const entry = commitSwap({ routes, setRoutes, setChangelog, a: aRoute, b: bRoute, notes: notes.trim() });
    setDone({ kind: 'swap', aDriver: aRoute.driverLogin, bDriver: bRoute.driverLogin, changes: entry.changes });
    setSwapA(''); setSwapB(''); setNotes('');
  };

  const sel = {
    width: '100%', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)',
    borderRadius: 8, padding: '8px 12px', color: 'var(--hp-text)', fontSize: '0.82rem', boxSizing: 'border-box',
  };
  const badge = { fontSize: '0.72rem', fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: 'rgba(201,168,76,0.12)', color: 'var(--hp-gold)' };

  if (isViewer) {
    return (
      <div style={{ background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 10, padding: 24, fontSize: '0.85rem', color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <AlertTriangle size={16} color="var(--hp-amber)" />
        Depot relocations and swaps are admin-only. Ask an administrator to make bay / chute / outlet changes.
      </div>
    );
  }

  if (done) {
    return (
      <div style={{ background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 10, padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--hp-green)' }}>
          <CheckCircle size={20} />
          <span style={{ fontWeight: 700 }}>
            {done.kind === 'relocate'
              ? `Round relocated — ${done.driver} (${done.adamCode})`
              : `Positions swapped — ${done.aDriver} ⇄ ${done.bDriver}`}
          </span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {(done.changes || []).map((c, i) => (
            <span key={i} style={badge}>
              {done.kind === 'relocate' ? `${c.field}: ${c.from || '—'} → ${c.to || '—'}` : `${c.field}: ${c.a} ⇄ ${c.b}`}
            </span>
          ))}
        </div>
        <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: '0.78rem', color: 'var(--hp-amber)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={14} />
          Logged as an AP-notifiable change. Go to the Change Log tab to generate the Australia Post notice.
        </div>
        <button onClick={() => setDone(null)} style={{ alignSelf: 'flex-start', background: 'var(--hp-gold)', color: '#000', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
          Make Another Change
        </button>
      </div>
    );
  }

  const segBtn = (active) => ({
    flex: 1, padding: '8px 12px', borderRadius: 7, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700,
    border: '1px solid ' + (active ? 'rgba(201,168,76,0.5)' : 'var(--hp-border)'),
    background: active ? 'rgba(201,168,76,0.14)' : 'var(--hp-surface)',
    color: active ? 'var(--hp-gold)' : 'var(--hp-dim)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => setMode('relocate')} style={segBtn(mode === 'relocate')}>
          <Move size={13} /> Relocate a round
        </button>
        <button onClick={() => setMode('swap')} style={segBtn(mode === 'swap')}>
          <ArrowRightLeft size={13} /> Swap two rounds
        </button>
      </div>

      <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, padding: '10px 14px', fontSize: '0.78rem', color: 'var(--hp-amber)', display: 'flex', gap: 8 }}>
        <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
        {mode === 'relocate'
          ? 'Move one round to a new bay / chute / outlet / wave at the depot. Streets stay with the round. Creates a change requiring Australia Post notification.'
          : 'Two rounds trade physical sort positions (chute, bay, outlet, wave). D# and streets stay with each round. Creates a change requiring Australia Post notification.'}
      </div>

      {/* ── Relocate ──────────────────────────────────────────────────────────── */}
      {mode === 'relocate' && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 480 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Round to relocate
            </label>
            <DriverCombobox value={target} onChange={pickTarget} options={routes} placeholder="Type a login or ADAM code…" showStreetCount />
          </div>

          {targetRoute && (
            <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: '0.82rem' }}>
                <strong style={{ color: 'var(--hp-text)' }}>{targetRoute.driverLogin}</strong>
                <span style={{ color: 'var(--hp-dim)', fontFamily: 'monospace', fontSize: '0.76rem' }}>{targetRoute.adamCode}</span>
                <Badge color={(AREA_COLORS[targetRoute.area] || AREA_COLORS.CITY).color} bg={(AREA_COLORS[targetRoute.area] || AREA_COLORS.CITY).bg}>{targetRoute.area}</Badge>
                <span style={{ color: 'var(--hp-dim)', fontSize: '0.76rem' }}>{targetRoute.streets.length} streets</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(90px, 100%), 1fr))', gap: 10 }}>
                {CODE_FIELDS.map(f => (
                  <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: '0.62rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{f.label}</span>
                    <input
                      value={draft[f.key] ?? ''}
                      onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))}
                      placeholder={targetRoute[f.key] && targetRoute[f.key] !== '—' ? targetRoute[f.key] : '—'}
                      style={{ ...sel, border: (String(draft[f.key] ?? '') !== String(targetRoute[f.key] ?? '')) ? '1px solid var(--hp-gold)' : '1px solid var(--hp-border)' }}
                    />
                  </label>
                ))}
              </div>
              {relocateChanges.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {relocateChanges.map((c, i) => (
                    <span key={i} style={badge}>{c.field}: {c.from || '—'} → {c.to || '—'}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 640 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Notes (optional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="e.g. Darlinghurst chute reallocation — round moved to outlet 22A" style={{ ...sel, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>

          <button
            onClick={doRelocate}
            disabled={!targetRoute || relocateChanges.length === 0}
            style={{
              background: (targetRoute && relocateChanges.length > 0) ? 'var(--hp-gold)' : 'var(--hp-border)',
              color: (targetRoute && relocateChanges.length > 0) ? '#000' : 'var(--hp-dim)',
              border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 700, fontSize: '0.85rem',
              cursor: (targetRoute && relocateChanges.length > 0) ? 'pointer' : 'default',
              alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <Move size={15} /> Apply Relocation
          </button>
        </>
      )}

      {/* ── Swap ──────────────────────────────────────────────────────────────── */}
      {mode === 'swap' && (
        <>
          <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Round A</label>
              <DriverCombobox value={swapA} onChange={setSwapA} options={routes.filter(r => r.adamCode !== swapB)} placeholder="Type a login or ADAM code…" showStreetCount />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Round B</label>
              <DriverCombobox value={swapB} onChange={setSwapB} options={routes.filter(r => r.adamCode !== swapA)} placeholder="Type a login or ADAM code…" showStreetCount />
            </div>
          </div>

          {aRoute && bRoute && (
            <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '14px 16px', overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 420, fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ color: 'var(--hp-dim)', textAlign: 'left' }}>
                    <th style={{ padding: '4px 8px', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Field</th>
                    <th style={{ padding: '4px 8px' }}>{aRoute.driverLogin} <span style={{ color: 'var(--hp-dim)', fontSize: '0.7rem' }}>(now → after)</span></th>
                    <th style={{ padding: '4px 8px' }}>{bRoute.driverLogin} <span style={{ color: 'var(--hp-dim)', fontSize: '0.7rem' }}>(now → after)</span></th>
                  </tr>
                </thead>
                <tbody>
                  {SWAP_FIELDS.map(k => (
                    <tr key={k} style={{ borderTop: '1px solid var(--hp-border)' }}>
                      <td style={{ padding: '6px 8px', color: 'var(--hp-dim)', fontWeight: 700 }}>{FIELD_LABEL[k]}</td>
                      <td style={{ padding: '6px 8px' }}>
                        <span style={{ color: 'var(--hp-dim)', textDecoration: 'line-through' }}>{aRoute[k] || '—'}</span>
                        {' → '}<span style={{ color: 'var(--hp-gold)', fontWeight: 700 }}>{bRoute[k] || '—'}</span>
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <span style={{ color: 'var(--hp-dim)', textDecoration: 'line-through' }}>{bRoute[k] || '—'}</span>
                        {' → '}<span style={{ color: 'var(--hp-gold)', fontWeight: 700 }}>{aRoute[k] || '—'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 640 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Notes (optional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="e.g. two rounds swapped chutes at the Darlinghurst depot" style={{ ...sel, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>

          <button
            onClick={doSwap}
            disabled={!aRoute || !bRoute}
            style={{
              background: (aRoute && bRoute) ? 'var(--hp-gold)' : 'var(--hp-border)',
              color: (aRoute && bRoute) ? '#000' : 'var(--hp-dim)',
              border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 700, fontSize: '0.85rem',
              cursor: (aRoute && bRoute) ? 'pointer' : 'default',
              alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <ArrowRightLeft size={15} /> Swap Positions
          </button>
        </>
      )}
    </div>
  );
}

// ── AP Notification Generator ─────────────────────────────────────────────────
function APNotification({ entry }) {
  const [copied, setCopied] = useState(false);

  let bodyLines = '';
  if (entry.type === 'edit') {
    bodyLines = `STREET RANGE AMENDMENT:\n  Previous entry: ${entry.oldStreet}\n  Updated entry:  ${entry.newStreet}`;
  } else if (entry.type === 'add') {
    bodyLines = `STREET ADDED TO ROUTE:\n  New entry: ${entry.street}`;
  } else if (entry.type === 'codes') {
    bodyLines = `SORT / DEPOT CODE UPDATE:\n${(entry.changes || []).map(c => `  ${c.field}: ${c.from || '—'} → ${c.to || '—'}`).join('\n')}`;
  } else if (entry.type === 'swap') {
    bodyLines = `DEPOT POSITION SWAP:\n  ${entry.aDriver} (${entry.aAdamCode})  ⇄  ${entry.bDriver} (${entry.bAdamCode})\n${(entry.changes || []).map(c => `  ${c.field}: ${c.a} ⇄ ${c.b}`).join('\n')}`;
  } else {
    bodyLines = `STREETS / ADDRESSES BEING TRANSFERRED (${entry.streets?.length || 0}):\n${(entry.streets || []).map((s, i) => `  ${i + 1}. ${s}`).join('\n')}`;
  }

  const text = `AUSTRALIA POST — ROUTE CHANGE NOTIFICATION
Date: ${fmtDate(entry.date)}
Reference: ${entry.id}

We wish to notify Australia Post of the following route address changes effective immediately:

AREA: ${entry.area}
DRIVER: ${entry.driverLogin || entry.fromDriver} (ADAM Code: ${entry.adamCode || entry.fromAdamCode})${entry.toDriver ? `\nTO DRIVER: ${entry.toDriver} (ADAM Code: ${entry.toAdamCode})` : ''}

${bodyLines}

${entry.notes ? `NOTES: ${entry.notes}\n` : ''}Please update your coding, framing, and chute/bay allocation systems accordingly.

Please confirm receipt and implementation of these changes.

Regards,
HollyPaul Operations`;

  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{
      background: 'var(--hp-surface)', border: '1px solid var(--hp-border)',
      borderRadius: 8, padding: 14, marginTop: 8,
    }}>
      <pre style={{
        fontSize: '0.72rem', color: 'var(--hp-text)', whiteSpace: 'pre-wrap',
        fontFamily: 'monospace', margin: 0, lineHeight: 1.7,
      }}>{text}</pre>
      <button onClick={copy} style={{
        marginTop: 10, background: copied ? 'var(--hp-green)' : 'var(--hp-surface)',
        border: '1px solid var(--hp-border)', color: copied ? '#000' : 'var(--hp-text)',
        borderRadius: 6, padding: '5px 14px', fontSize: '0.75rem',
        fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
      }}>
        {copied ? <CheckCircle size={12} /> : <Copy size={12} />}
        {copied ? 'Copied!' : 'Copy to Clipboard'}
      </button>
    </div>
  );
}

// ── Change Log Tab ────────────────────────────────────────────────────────────
function ChangeLogTab({ changelog, setChangelog }) {
  const [apOpen, setApOpen] = useState(null);

  const markNotified = async (id) => {
    const now = new Date().toISOString();
    const updated = changelog.map(e =>
      e.id === id ? { ...e, apNotified: true, apNotifiedDate: now } : e
    );
    setChangelog(updated);
    setApOpen(null);
    try { await apiPost({ action: 'updateAPStatus', id, apNotified: true }); } catch {}
  };

  const markUnnotified = async (id) => {
    const updated = changelog.map(e =>
      e.id === id ? { ...e, apNotified: false, apNotifiedDate: null } : e
    );
    setChangelog(updated);
    try { await apiPost({ action: 'updateAPStatus', id, apNotified: false }); } catch {}
  };

  if (changelog.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--hp-dim)', fontSize: '0.85rem' }}>
        No route changes recorded yet. Use the Transfer tab to move streets between drivers, or edit/add streets on the Routes tab.
      </div>
    );
  }

  // Helper to render a type badge
  const TypeBadge = ({ type }) => {
    const map = {
      transfer: { label: 'Transfer', color: '#60a5fa', bg: 'rgba(59,130,246,0.12)' },
      edit:     { label: 'Range Edit', color: 'var(--hp-gold)', bg: 'rgba(201,168,76,0.12)' },
      add:      { label: 'Street Added', color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
      codes:    { label: 'Depot Codes', color: '#f472b6', bg: 'rgba(244,114,182,0.12)' },
      swap:     { label: 'Position Swap', color: '#a78bfa', bg: 'rgba(139,92,246,0.15)' },
    };
    const t = map[type] || map.transfer;
    return <Badge color={t.color} bg={t.bg}>{t.label}</Badge>;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {changelog.map(entry => {
        const ac = AREA_COLORS[entry.area] || AREA_COLORS.CITY;
        const showAP = apOpen === entry.id;
        const entryType = entry.type || 'transfer';

        return (
          <div key={entry.id} style={{
            background: 'var(--hp-card)', border: '1px solid var(--hp-border)',
            borderRadius: 10, overflow: 'hidden',
          }}>
            <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {/* ID, area, type, date */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--hp-dim)' }}>{entry.id}</span>
                  <Badge color={ac.color} bg={ac.bg}>{entry.area}</Badge>
                  <TypeBadge type={entryType} />
                  <span style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>{fmtDate(entry.date)}</span>
                </div>

                {/* Change description */}
                {entryType === 'transfer' && (
                  <div style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <strong>{entry.fromDriver}</strong>
                    <span style={{ color: 'var(--hp-dim)', fontSize: '0.7rem' }}>({entry.fromAdamCode})</span>
                    <ArrowRightLeft size={12} color="var(--hp-dim)" />
                    <strong>{entry.toDriver}</strong>
                    <span style={{ color: 'var(--hp-dim)', fontSize: '0.7rem' }}>({entry.toAdamCode})</span>
                  </div>
                )}
                {entryType === 'edit' && (
                  <div style={{ fontSize: '0.82rem', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span><strong>{entry.driverLogin}</strong> <span style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}>({entry.adamCode})</span></span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: '0.76rem' }}>
                      <span style={{ color: 'var(--hp-dim)', textDecoration: 'line-through' }}>{entry.oldStreet}</span>
                      <ArrowRight size={11} color="var(--hp-dim)" />
                      <span style={{ color: 'var(--hp-gold)', fontWeight: 600 }}>{entry.newStreet}</span>
                    </div>
                  </div>
                )}
                {entryType === 'add' && (
                  <div style={{ fontSize: '0.82rem', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span><strong>{entry.driverLogin}</strong> <span style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}>({entry.adamCode})</span></span>
                    <div style={{ fontSize: '0.76rem' }}>
                      Added: <span style={{ color: '#34d399', fontWeight: 600 }}>{entry.street}</span>
                    </div>
                  </div>
                )}
                {entryType === 'codes' && (
                  <div style={{ fontSize: '0.82rem', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span><strong>{entry.driverLogin}</strong> <span style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}>({entry.adamCode})</span></span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: '0.76rem' }}>
                      {(entry.changes || []).map((c, i) => (
                        <span key={i}>
                          {c.field}: <span style={{ color: 'var(--hp-dim)', textDecoration: 'line-through' }}>{c.from || '—'}</span>
                          {' '}<ArrowRight size={9} style={{ display: 'inline', verticalAlign: 'middle' }} color="var(--hp-dim)" />{' '}
                          <span style={{ color: '#f472b6', fontWeight: 600 }}>{c.to || '—'}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {entryType === 'swap' && (
                  <div style={{ fontSize: '0.82rem', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <strong>{entry.aDriver}</strong>
                      <span style={{ color: 'var(--hp-dim)', fontSize: '0.7rem' }}>({entry.aAdamCode})</span>
                      <ArrowRightLeft size={12} color="var(--hp-dim)" />
                      <strong>{entry.bDriver}</strong>
                      <span style={{ color: 'var(--hp-dim)', fontSize: '0.7rem' }}>({entry.bAdamCode})</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: '0.76rem', color: 'var(--hp-dim)' }}>
                      {(entry.changes || []).map((c, i) => (
                        <span key={i}>{c.field}: <span style={{ color: 'var(--hp-text)' }}>{c.a} ⇄ {c.b}</span></span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Streets preview for transfers */}
                {entryType === 'transfer' && (
                  <>
                    <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>
                      {entry.streets.length} street{entry.streets.length > 1 ? 's' : ''} transferred
                      {entry.notes ? ` · ${entry.notes}` : ''}
                    </div>
                    <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {entry.streets.slice(0, 5).map((s, i) => (
                        <span key={i} style={{
                          background: 'var(--hp-surface)', border: '1px solid var(--hp-border)',
                          borderRadius: 4, fontSize: '0.68rem', padding: '2px 6px', color: 'var(--hp-dim)',
                        }}>{s}</span>
                      ))}
                      {entry.streets.length > 5 && (
                        <span style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', alignSelf: 'center' }}>
                          +{entry.streets.length - 5} more
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* AP Status */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                {entry.apNotified ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--hp-green)', fontSize: '0.78rem', fontWeight: 700 }}>
                      <CheckCircle size={14} />
                      AP Notified
                    </div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)' }}>{fmtDate(entry.apNotifiedDate)}</div>
                    <button
                      onClick={() => markUnnotified(entry.id)}
                      style={{ background: 'none', border: '1px solid var(--hp-border)', color: 'var(--hp-dim)', fontSize: '0.65rem', padding: '2px 8px', borderRadius: 5, cursor: 'pointer' }}
                    >
                      Undo
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--hp-amber)', fontSize: '0.78rem', fontWeight: 700 }}>
                      <Clock size={14} />
                      AP Pending
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => setApOpen(showAP ? null : entry.id)}
                        style={{
                          background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)',
                          color: '#60a5fa', fontSize: '0.72rem', padding: '4px 10px',
                          borderRadius: 6, cursor: 'pointer', fontWeight: 700,
                          display: 'flex', alignItems: 'center', gap: 5,
                        }}
                      >
                        <FileText size={11} />
                        {showAP ? 'Hide' : 'AP Notice'}
                      </button>
                      <button
                        onClick={() => markNotified(entry.id)}
                        style={{
                          background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)',
                          color: 'var(--hp-green)', fontSize: '0.72rem', padding: '4px 10px',
                          borderRadius: 6, cursor: 'pointer', fontWeight: 700,
                          display: 'flex', alignItems: 'center', gap: 5,
                        }}
                      >
                        <CheckCircle size={11} />
                        Mark Notified
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* AP Notification Panel */}
            {showAP && (
              <div style={{ borderTop: '1px solid var(--hp-border)', padding: '12px 16px', background: 'var(--hp-surface)' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginBottom: 6, fontWeight: 700 }}>
                  AUSTRALIA POST NOTIFICATION TEMPLATE
                </div>
                <APNotification entry={entry} />
                <button
                  onClick={() => markNotified(entry.id)}
                  style={{
                    marginTop: 12, background: 'var(--hp-green)', border: 'none',
                    color: '#000', borderRadius: 7, padding: '7px 18px',
                    fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <CheckCircle size={13} />
                  Mark as Notified — AP
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Download Routes ───────────────────────────────────────────────────────────
function downloadRoutes(routes) {
  const lines = ['Driver Login,Area,ADAM Code,Bay Number,Chute,Wave,Street / Location'];
  routes.forEach(r => {
    if (r.streets.length === 0) {
      lines.push([r.driverLogin, r.area, r.adamCode, r.bayNumber, r.chute, r.wave, ''].join(','));
    } else {
      r.streets.forEach(s => {
        lines.push([
          r.driverLogin, r.area, r.adamCode, r.bayNumber, r.chute, r.wave,
          `"${s.replace(/"/g, '""')}"`,
        ].join(','));
      });
    }
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `hollypaul_routes_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DriverRoutesPage() {
  const { currentUser } = useApp();
  const isViewer = currentUser?.role === 'viewer';
  const [routes, setRoutes]         = useState(INITIAL_ROUTES);
  const [changelog, setChangelog]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [needsInit, setNeedsInit]   = useState(false);
  // ?tab= / ?driver= deep links (Onboarding task → "assign his streets"). The driver
  // seeds the search box, which is what filters the Routes tab to one round.
  const [searchParams] = useSearchParams();
  const tabParam = (searchParams.get('tab') || '').trim();
  const driverParam = (searchParams.get('driver') || '').toUpperCase();
  const [tab, setTab]               = useState(
    ['routes', 'transfer', 'relocate-swap', 'changelog', 'route-sections', 'driver-complete', 'section-scan'].includes(tabParam) ? tabParam : 'routes');
  const [search, setSearch]         = useState(driverParam);
  const [areaFilter, setAreaFilter] = useState('ALL');
  const [renameModal, setRenameModal] = useState(false);
  const [renameOld, setRenameOld]     = useState('');
  const [renameNew, setRenameNew]     = useState('');
  const [renameBusy, setRenameBusy]   = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [newLogin, setNewLogin]       = useState('');
  const [newArea, setNewArea]         = useState('CITY');
  const [newAdam, setNewAdam]         = useState('');
  const [createBusy, setCreateBusy]   = useState(false);
  // The active driver registry, from the same load as the routes. Needed because a driver
  // who owns no route row is invisible to anything derived from `routes` alone.
  const [driverRegistry, setDriverRegistry] = useState([]);
  // Route Sections ↔ Driver Complete share one parsed audit week (session-only).
  const [parsedWeek, setParsedWeek]     = useState(null);  // { contract, events, drivers, days }
  const [rsFocusDriver, setRsFocusDriver] = useState('');  // driver to pre-select when jumping from Complete

  useEffect(() => {
    apiGet()
      .then(data => {
        if (data.success) {
          setDriverRegistry(data.drivers || []);
          if (data.routes && data.routes.length > 0) {
            setRoutes(data.routes);
            setChangelog(data.changelog || []);
          } else {
            setNeedsInit(true);
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const initSheet = async () => {
    try {
      await apiPost({ action: 'initDriverRoutes', routes: INITIAL_ROUTES });
      setRoutes(INITIAL_ROUTES);
      setChangelog([]);
      setNeedsInit(false);
    } catch (e) {
      alert('Failed to initialise sheet: ' + e.message);
    }
  };

  const stats = useMemo(() => {
    const drivers = new Set(routes.map(r => r.driverLogin)).size;
    const streets = routes.reduce((n, r) => n + r.streets.length, 0);
    const pending = changelog.filter(c => !c.apNotified).length;
    return { drivers, streets, pending };
  }, [routes, changelog]);

  // Driver registry for Route Sections / Driver Complete, built live from the
  // Driver_Routes rows mapped to the array-of-arrays column order the engine expects:
  //   0 ADAM · 1 Login · 2 Area · 3 D# · 4 Chute · 5 Bay · 6 Wave · 7 Outlet · 8 Streets JSON
  const registry = useMemo(() => buildRegistryFromRoutes(
    routes.map(r => [
      r.adamCode, r.driverLogin, r.area, r.dNumber,
      r.chute, r.bayNumber, r.wave, r.outlet, JSON.stringify(r.streets || []),
    ]),
    { areaContract: { DARL: 'N22000' } }
  ), [routes]);

  // Inline route-list repair from the Route Sections review flags. 'add' appends the street to
  // the driver; 'move' also removes it from the current owner. Persists live + logs for AP,
  // reusing the same saveRouteTransfer + changelog path as the Routes/Transfer tabs.
  const repairStreet = useCallback(({ type, driver, street, streets, fromDriver }) => {
    const norm = (s) => String(s).trim().toLowerCase();
    const target = routes.find(r => r.driverLogin.toUpperCase() === String(driver).toUpperCase());
    if (!target) return;

    // 'assign': address-level reassignment (e.g. out-of-range house numbers on a shared street).
    // Logs a transfer of the specific addresses for AP; route lists/ranges are NOT changed.
    if (type === 'assign') {
      const from = fromDriver ? routes.find(r => r.driverLogin.toUpperCase() === String(fromDriver).toUpperCase()) : null;
      if (!from || !(streets && streets.length)) return;
      const now = new Date().toISOString();
      const entry = {
        id: `CHG-${Date.now()}`, date: now, type: 'transfer', streets,
        fromAdamCode: from.adamCode, fromDriver: from.driverLogin,
        toAdamCode: target.adamCode, toDriver: target.driverLogin, area: target.area,
        notes: 'Route Sections — address-level reassignment (ranges unchanged)',
        apNotified: false, apNotifiedDate: null,
      };
      setChangelog(prev => [entry, ...prev]);
      apiPost({ action: 'saveRouteTransfer', entry }).catch(() => {}); // entry only — no route rewrite
      return;
    }
    const owner = type === 'move' && fromDriver
      ? routes.find(r => r.driverLogin.toUpperCase() === String(fromDriver).toUpperCase())
      : null;
    // One street or a whole section — same path. A section moved as N separate calls would
    // rewrite the routes N times and give AP N entries for what is one decision; as a list
    // it's one write and one entry naming every street.
    const list = (streets && streets.length ? streets : (street ? [street] : []))
      .filter((s, i, a) => a.findIndex(x => norm(x) === norm(s)) === i);
    if (!list.length) return;
    const updated = routes.map(r => {
      if (r.adamCode === target.adamCode) {
        const add = list.filter(s => !(r.streets || []).some(x => norm(x) === norm(s))); // skip any already on the list
        return add.length ? { ...r, streets: [...(r.streets || []), ...add] } : r;
      }
      if (owner && r.adamCode === owner.adamCode) {
        return { ...r, streets: (r.streets || []).filter(s => !list.some(x => norm(x) === norm(s))) };
      }
      return r;
    });
    setRoutes(updated);
    const now = new Date().toISOString();
    const many = list.length > 1;
    const entry = owner
      ? { id: `CHG-${Date.now()}`, date: now, type: 'transfer',
          streets: list, fromAdamCode: owner.adamCode, fromDriver: owner.driverLogin,
          toAdamCode: target.adamCode, toDriver: target.driverLogin,
          area: target.area,
          notes: many ? `Route Sections — section move, ${list.length} streets` : 'Route Sections review',
          apNotified: false, apNotifiedDate: null }
      : { id: `CHG-${Date.now()}`, date: now, type: 'add',
          adamCode: target.adamCode, driverLogin: target.driverLogin, area: target.area,
          street: list[0], streets: list,   // streets[]/toDriver are what persist (see saveEdit)
          toAdamCode: target.adamCode, toDriver: target.driverLogin,
          notes: many ? `Route Sections — ${list.length} streets added` : 'Route Sections review',
          apNotified: false, apNotifiedDate: null };
    setChangelog(prev => [entry, ...prev]);
    apiPost({ action: 'saveRouteTransfer', routes: updated, entry }).catch(() => {});
  }, [routes]);

  // Driver Complete roster — same registry + the shared parsed week (no re-parse).
  const darlRoster = useMemo(() => {
    const events = parsedWeek?.events || [];
    return Object.keys(registry)
      .filter(d => registry[d].area === 'DARL')
      .map(driver => {
        const dataLoaded = events.some(e => e.driver === driver);
        const plan = buildPlan(events, driver, registry[driver]);
        const nom = nominateSections(plan.streets);
        const flags = detectFlags(nom, registry[driver], groupSections(nom), driver);
        return { driver, dataLoaded, parcels: nom.reduce((a, s) => a + s.parcels, 0), flags };
      });
  }, [registry, parsedWeek]);

  const resetToMaster = async () => {
    if (!window.confirm('Reset all routes to the original master data? This will overwrite the Google Sheet.')) return;
    try {
      await apiPost({ action: 'initDriverRoutes', routes: INITIAL_ROUTES });
      setRoutes(INITIAL_ROUTES);
      setChangelog([]);
    } catch {
      alert('Failed to reset. Check your connection.');
    }
  };

  const handleRename = async (e) => {
    e.preventDefault();
    const oldLogin = renameOld.trim().toUpperCase();
    const newLogin = renameNew.trim().toUpperCase();
    if (!oldLogin || !newLogin || oldLogin === newLogin) return;
    const affected = routes.filter(r => r.driverLogin.toUpperCase() === oldLogin);
    if (affected.length === 0) { alert(`No routes found for login "${oldLogin}".`); return; }
    if (!window.confirm(`Rename "${oldLogin}" → "${newLogin}" across ${affected.length} route(s)? This will update the live sheet.`)) return;
    setRenameBusy(true);
    try {
      const updated = routes.map(r =>
        r.driverLogin.toUpperCase() === oldLogin ? { ...r, driverLogin: newLogin } : r
      );
      await apiPost({ action: 'renameDriverLogin', oldLogin, newLogin, routes: updated });
      setRoutes(updated);
      setRenameModal(false);
      setRenameOld('');
      setRenameNew('');
    } catch (err) {
      alert('Rename failed: ' + err.message);
    } finally {
      setRenameBusy(false);
    }
  };

  const handleCreateDriver = async (e) => {
    e.preventDefault();
    const login = newLogin.trim().toUpperCase();
    if (!login) return;
    // ADAM code: use what's typed, else a placeholder keyed off the login + area.
    const adamCode = (newAdam.trim() || `NEW_${login}_${newArea}`).toUpperCase();
    if (routes.some(r => r.adamCode === adamCode)) {
      alert(`A route with ADAM code "${adamCode}" already exists. Enter a different code.`);
      return;
    }
    const existingLogin = routes.some(r => r.driverLogin.toUpperCase() === login);
    if (existingLogin && !window.confirm(`Login "${login}" already has route(s). Add another empty route entry for them?`)) return;
    setCreateBusy(true);
    try {
      const newRoute = {
        adamCode, driverLogin: login, area: newArea,
        dNumber: '', chute: '', bayNumber: '', wave: '', outlet: '', streets: [],
      };
      const updated = [...routes, newRoute];
      // saveRouteTransfer rewrites the whole routes table (one row per driver),
      // so a 0-street driver persists. No changelog entry — creating a driver is
      // not an AP-notifiable street change.
      await apiPost({ action: 'saveRouteTransfer', routes: updated });
      setRoutes(updated);
      setCreateModal(false);
      setNewLogin(''); setNewArea('CITY'); setNewAdam('');
    } catch (err) {
      alert('Create driver failed: ' + err.message);
    } finally {
      setCreateBusy(false);
    }
  };

  const TABS = [
    { id: 'routes',    label: 'Routes',     icon: Route },
    { id: 'transfer',  label: 'Transfer',   icon: ArrowRightLeft },
    { id: 'relocate-swap', label: 'Relocate & Swap', icon: Building2 },
    { id: 'changelog', label: `Change Log${changelog.filter(c => !c.apNotified).length > 0 ? ` (${changelog.filter(c => !c.apNotified).length})` : ''}`, icon: ClipboardList },
    { id: 'route-sections',  label: 'Route Sections',  icon: LayoutGrid },
    { id: 'driver-complete', label: 'Driver Complete',  icon: CheckSquare },
    { id: 'section-scan',    label: 'Section Scan',     icon: ScanLine },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '0 4px' }}>
      {/* Loading / init banners */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '12px', fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
          Loading routes from Google Sheets…
        </div>
      )}
      {!loading && needsInit && (
        <div style={{
          background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)',
          borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
          fontSize: '0.8rem', color: '#60a5fa',
        }}>
          <AlertTriangle size={15} />
          <span>Google Sheet is empty. Click to load the master routes data from the bundled spreadsheet.</span>
          <button onClick={initSheet} style={{
            marginLeft: 'auto', background: '#60a5fa', color: '#000', border: 'none',
            borderRadius: 6, padding: '6px 14px', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer',
          }}>
            Initialise Sheet
          </button>
        </div>
      )}

      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--hp-gold)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Route size={22} /> Driver Routes
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 3 }}>
            Manage driver route assignments, street transfers, and Australia Post notifications
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!isViewer && (
            <button onClick={() => { setNewLogin(''); setNewArea('CITY'); setNewAdam(''); setCreateModal(true); }} style={{
              background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.35)',
              color: 'var(--hp-gold)', borderRadius: 7, padding: '7px 14px',
              fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <UserPlus size={13} /> Create Driver
            </button>
          )}
          <button onClick={() => { setRenameOld(''); setRenameNew(''); setRenameModal(true); }} style={{
            background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.35)',
            color: 'var(--hp-gold)', borderRadius: 7, padding: '7px 14px',
            fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Pencil size={13} /> Rename Login
          </button>
          <button onClick={() => downloadRoutes(routes)} style={{
            background: 'var(--hp-surface)', border: '1px solid var(--hp-border)',
            color: 'var(--hp-text)', borderRadius: 7, padding: '7px 14px',
            fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Download size={13} /> Export CSV
          </button>
          <button onClick={resetToMaster} style={{
            background: 'var(--hp-surface)', border: '1px solid var(--hp-border)',
            color: 'var(--hp-dim)', borderRadius: 7, padding: '7px 14px',
            fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <RefreshCw size={13} /> Reset to Master
          </button>
        </div>
      </div>

      {/* AP Pending alert */}
      {stats.pending > 0 && (
        <div style={{
          background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)',
          borderRadius: 8, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10,
          fontSize: '0.8rem', color: 'var(--hp-amber)',
        }}>
          <AlertTriangle size={15} />
          <span>
            <strong>{stats.pending}</strong> route change{stats.pending > 1 ? 's have' : ' has'} not yet been notified to Australia Post.{' '}
            <button
              onClick={() => setTab('changelog')}
              style={{ background: 'none', border: 'none', color: 'var(--hp-amber)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 'inherit' }}
            >
              View Change Log
            </button>
          </span>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))', gap: 12 }}>
        <StatCard icon={Users}         label="Active Drivers"  value={stats.drivers}         color="var(--hp-gold)" />
        <StatCard icon={MapPin}        label="Total Streets"   value={stats.streets}          color="#60a5fa" />
        <StatCard icon={ClipboardList} label="Total Changes"   value={changelog.length}       color="#a78bfa" />
        <StatCard icon={AlertTriangle} label="AP Pending"      value={stats.pending}          color={stats.pending > 0 ? 'var(--hp-amber)' : 'var(--hp-dim)'} />
      </div>

      {/* Search + area filter */}
      {tab === 'routes' && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--hp-dim)' }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search driver, street, ADAM code, bay..."
              style={{
                width: '100%', paddingLeft: 32, paddingRight: 12, paddingTop: 8, paddingBottom: 8,
                background: 'var(--hp-surface)', border: '1px solid var(--hp-border)',
                borderRadius: 8, color: 'var(--hp-text)', fontSize: '0.82rem',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {['ALL', 'CITY', 'DARL', 'BWAY'].map(a => {
              const ac = AREA_COLORS[a];
              const active = areaFilter === a;
              return (
                <button key={a} onClick={() => setAreaFilter(a)} style={{
                  background: active ? (ac ? ac.bg : 'rgba(201,168,76,0.15)') : 'var(--hp-surface)',
                  border: `1px solid ${active ? (ac ? ac.color : 'var(--hp-gold)') : 'var(--hp-border)'}`,
                  color: active ? (ac ? ac.color : 'var(--hp-gold)') : 'var(--hp-dim)',
                  borderRadius: 7, padding: '6px 14px', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer',
                }}>
                  {a}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid var(--hp-border)', display: 'flex', gap: 0 }}>
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)} style={{
            background: 'none', border: 'none', borderBottom: tab === id ? '2px solid var(--hp-gold)' : '2px solid transparent',
            color: tab === id ? 'var(--hp-gold)' : 'var(--hp-dim)',
            padding: '8px 18px', fontWeight: tab === id ? 700 : 500, fontSize: '0.82rem',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, marginBottom: -1,
          }}>
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'routes' && (
        <RoutesTab
          routes={routes}
          setRoutes={setRoutes}
          setChangelog={setChangelog}
          search={search}
          areaFilter={areaFilter}
          onRenameLogin={isViewer ? undefined : (login => { setRenameOld(login); setRenameNew(''); setRenameModal(true); })}
        />
      )}
      {tab === 'transfer' && (
        <TransferTab
          routes={routes} setRoutes={setRoutes}
          changelog={changelog} setChangelog={setChangelog}
          isViewer={isViewer} requestedBy={currentUser?.login}
          driverRegistry={driverRegistry}
          onCreateDriver={() => { setNewLogin(''); setNewArea('CITY'); setNewAdam(''); setCreateModal(true); }}
        />
      )}
      {tab === 'relocate-swap' && (
        <RelocateSwapTab
          routes={routes} setRoutes={setRoutes}
          changelog={changelog} setChangelog={setChangelog}
          isViewer={isViewer}
        />
      )}
      {tab === 'changelog' && (
        <ChangeLogTab changelog={changelog} setChangelog={setChangelog} />
      )}

      {(tab === 'route-sections' || tab === 'driver-complete') && (
        <div style={{ marginTop: 18 }}>
          {/* Engine stays mounted across both tabs (hidden on Complete) so the parsed
              week + selected driver survive the jump from Driver Complete. */}
          <div style={{ display: tab === 'route-sections' ? 'block' : 'none' }}>
            <RouteSectionsEngine
              registry={registry}
              focusDriver={rsFocusDriver}
              onParsed={setParsedWeek}
              login={currentUser?.login}
              onRepair={isViewer ? undefined : repairStreet}
            />
          </div>
          {tab === 'driver-complete' && (
            <DriverCompletePage
              roster={darlRoster}
              area="DARL"
              contract="N22000"
              onOpenDriver={d => { setRsFocusDriver(d); setTab('route-sections'); }}
            />
          )}
        </div>
      )}

      {tab === 'section-scan' && (
        <div style={{ marginTop: 18 }}>
          <SectionScanReport login={currentUser?.login} />
        </div>
      )}

      {/* ── Rename Login Modal ──────────────────────────────────────────────── */}
      {renameModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 300,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}>
          <div style={{
            background: 'var(--hp-card)', border: '1px solid var(--hp-border)',
            borderRadius: 'var(--radius-lg)', padding: 28, width: '100%', maxWidth: 420,
            boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--hp-gold)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Pencil size={16} /> Rename Driver Login
              </div>
              <button onClick={() => setRenameModal(false)} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: 20 }}>
              Use this when Australia Post creates a new login ID for an existing driver. All routes assigned to the old login will be updated to the new one.
            </p>
            <form onSubmit={handleRename} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
                  Current (old) login ID
                </label>
                <select
                  value={renameOld}
                  onChange={e => setRenameOld(e.target.value)}
                  required
                  style={{ width: '100%', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '9px 12px', color: 'var(--hp-text)', fontSize: '0.88rem', fontFamily: 'var(--font-mono)', boxSizing: 'border-box' }}
                >
                  <option value="">— Select driver —</option>
                  {[...new Set(routes.map(r => r.driverLogin))].filter(Boolean).sort().map(l => (
                    <option key={l} value={l}>{l} ({routes.filter(r => r.driverLogin === l).length} route{routes.filter(r => r.driverLogin === l).length !== 1 ? 's' : ''})</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
                  New login ID (from Australia Post)
                </label>
                <input
                  value={renameNew}
                  onChange={e => setRenameNew(e.target.value.toUpperCase())}
                  placeholder="e.g. SHARLANDS2"
                  required
                  autoCapitalize="characters"
                  style={{ width: '100%', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '9px 12px', color: 'var(--hp-text)', fontSize: '0.88rem', fontFamily: 'var(--font-mono)', boxSizing: 'border-box' }}
                />
              </div>
              {renameOld && renameNew && renameOld.toUpperCase() !== renameNew.toUpperCase() && (
                <div style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
                  <strong style={{ color: 'var(--hp-text)' }}>{renameOld.toUpperCase()}</strong>
                  {' → '}
                  <strong style={{ color: 'var(--hp-gold)' }}>{renameNew.toUpperCase()}</strong>
                  {'  ·  '}
                  {routes.filter(r => r.driverLogin.toUpperCase() === renameOld.toUpperCase()).length} route(s) will be updated
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button type="button" onClick={() => setRenameModal(false)} style={{
                  flex: 1, padding: '10px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)',
                  borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
                }}>
                  Cancel
                </button>
                <button type="submit" disabled={renameBusy || !renameOld || !renameNew || renameOld.toUpperCase() === renameNew.toUpperCase()} style={{
                  flex: 1, padding: '10px', background: 'var(--hp-gold)', border: 'none',
                  borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 700, fontSize: '0.875rem',
                  cursor: renameBusy ? 'not-allowed' : 'pointer', opacity: renameBusy ? 0.7 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}>
                  <Check size={15} /> {renameBusy ? 'Saving…' : 'Rename'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Create Driver Modal ─────────────────────────────────────────────── */}
      {createModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 300,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}>
          <div style={{
            background: 'var(--hp-card)', border: '1px solid var(--hp-border)',
            borderRadius: 'var(--radius-lg)', padding: 28, width: '100%', maxWidth: 420,
            boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--hp-gold)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <UserPlus size={16} /> Create New Driver
              </div>
              <button onClick={() => setCreateModal(false)} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: 20 }}>
              Add a driver with a new Australia Post login. They start with no streets and immediately become available as a transfer recipient, so you can move streets to them.
            </p>
            <form onSubmit={handleCreateDriver} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
                  Driver login ID
                </label>
                <input
                  value={newLogin}
                  onChange={e => setNewLogin(e.target.value.toUpperCase())}
                  placeholder="e.g. GALLEGUIL"
                  required
                  autoFocus
                  autoCapitalize="characters"
                  style={{ width: '100%', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '9px 12px', color: 'var(--hp-text)', fontSize: '0.88rem', fontFamily: 'var(--font-mono)', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
                  Area
                </label>
                <select
                  value={newArea}
                  onChange={e => setNewArea(e.target.value)}
                  style={{ width: '100%', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '9px 12px', color: 'var(--hp-text)', fontSize: '0.88rem', boxSizing: 'border-box' }}
                >
                  {['CITY', 'BWAY', 'DARL'].map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
                  ADAM / Bay code <span style={{ textTransform: 'none', fontWeight: 400 }}>(optional)</span>
                </label>
                <input
                  value={newAdam}
                  onChange={e => setNewAdam(e.target.value.toUpperCase())}
                  placeholder="e.g. BAY_330_CITY_D4 — leave blank to auto-generate"
                  autoCapitalize="characters"
                  style={{ width: '100%', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '9px 12px', color: 'var(--hp-text)', fontSize: '0.88rem', fontFamily: 'var(--font-mono)', boxSizing: 'border-box' }}
                />
                <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 6 }}>
                  Each driver round needs a unique ADAM code. If blank, a placeholder is used — you can set the real code later on the Routes tab.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button type="button" onClick={() => setCreateModal(false)} style={{
                  flex: 1, padding: '10px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)',
                  borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
                }}>
                  Cancel
                </button>
                <button type="submit" disabled={createBusy || !newLogin.trim()} style={{
                  flex: 1, padding: '10px', background: 'var(--hp-gold)', border: 'none',
                  borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 700, fontSize: '0.875rem',
                  cursor: createBusy ? 'not-allowed' : 'pointer', opacity: createBusy ? 0.7 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}>
                  <Check size={15} /> {createBusy ? 'Creating…' : 'Create Driver'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
